import {useCallback, useEffect, useRef, useState} from "react";

const VERSION_URL = `${process.env.PUBLIC_URL}/version.json`;
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const APPLIED_KEY = "app-update-applied";
const APPLIED_TTL = 10 * 60 * 1000; // ignore update signals for 10 min after a refresh

const fetchVersion = async () => {
    const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: "no-store",
        headers: {"Cache-Control": "no-cache"},
    });

    if (!response.ok) {
        throw new Error(`version.json request failed with ${response.status}`);
    }

    return response.json();
};

// Remembers (per tab, survives reload) that the user already hit "Refresh now"
const readApplied = () => {
    try {
        const value = JSON.parse(sessionStorage.getItem(APPLIED_KEY));
        if (value && Date.now() - value.at < APPLIED_TTL) return value;
    } catch (e) {
        /* storage unavailable */
    }
    return null;
};

const writeApplied = (buildId) => {
    try {
        sessionStorage.setItem(APPLIED_KEY, JSON.stringify({buildId, at: Date.now()}));
    } catch (e) {
        /* storage unavailable */
    }
};

/**
 * @function hardReload
 * @description unregisters service workers + clears caches so the reload is
 * guaranteed to fetch the new build from the network instead of the old precache
 */
const hardReload = async () => {
    try {
        const registrations = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
        await Promise.all(registrations.map((registration) => registration.unregister()));

        if (window.caches) {
            const keys = await caches.keys();
            await Promise.all(keys.map((key) => caches.delete(key)));
        }
    } catch (e) {
        /* reload anyway */
    }
    window.location.reload();
};

export const useAppUpdate = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const registrationRef = useRef(null);
    const currentBuildIdRef = useRef(null);
    const latestBuildIdRef = useRef(null);
    const appliedRef = useRef(false);

    const applyUpdate = useCallback(async () => {
        if (appliedRef.current) return;
        appliedRef.current = true;

        // remember which build we're moving to, so the modal doesn't re-open after reload
        let latest = latestBuildIdRef.current;
        if (!latest) {
            try {
                latest = (await fetchVersion()).buildId;
            } catch (e) {
                /* ignore */
            }
        }
        writeApplied(latest ?? "unknown");

        const registration =
            registrationRef.current ?? (await navigator.serviceWorker?.getRegistration?.());
        const waitingWorker = registration?.waiting;

        // No waiting worker (polling-only signal, SW unsupported, or another tab
        // already activated it): force a network reload.
        if (!waitingWorker) {
            hardReload();
            return;
        }

        let reloaded = false;
        navigator.serviceWorker.addEventListener("controllerchange", () => {
            if (reloaded) return;
            reloaded = true;
            window.location.reload();
        });
        waitingWorker.postMessage({type: "SKIP_WAITING"});

        // Safety net if controllerchange never fires
        setTimeout(() => {
            if (!reloaded) {
                reloaded = true;
                hardReload();
            }
        }, 3000);
    }, []);

    useEffect(() => {
        // --- Signal 1: service worker install event ---
        const handleSwUpdate = (event) => {
            if (readApplied()) return; // user just refreshed, don't ask again
            registrationRef.current = event.detail;
            setUpdateAvailable(true);
        };
        window.addEventListener("sw-update-available", handleSwUpdate);

        // --- Signal 2: poll version.json ---
        let intervalId;

        const checkVersion = async () => {
            try {
                const data = await fetchVersion();
                latestBuildIdRef.current = data.buildId;

                if (currentBuildIdRef.current === null) {
                    currentBuildIdRef.current = data.buildId; // baseline on first load
                    return;
                }

                if (data.buildId !== currentBuildIdRef.current) {
                    const applied = readApplied();
                    if (applied && applied.buildId === data.buildId) return;

                    clearInterval(intervalId);

                    if (data.isSilent) {
                        applyUpdate();
                    } else {
                        setUpdateAvailable(true);
                    }
                }
            } catch (error) {
                console.warn("Version check failed:", error.message);
            }
        };

        checkVersion();
        intervalId = setInterval(checkVersion, POLL_INTERVAL);

        const onVisible = () => {
            if (document.visibilityState === "visible") checkVersion();
        };
        document.addEventListener("visibilitychange", onVisible);

        return () => {
            window.removeEventListener("sw-update-available", handleSwUpdate);
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [applyUpdate]);

    return {updateAvailable, applyUpdate};
};

export default useAppUpdate;
